<template>
  <!-- Hover-expand desktop drawer: overlays content, takes no layout space -->
  <aside
    class="hidden lg:block fixed top-16 left-0 bottom-0 z-40 group pointer-events-none w-3"
    aria-label="Primary navigation"
  >
    <!-- Accessible focus handle -->
    <button
      class="sr-only focus:not-sr-only absolute left-0 top-4 z-50 btn btn-ghost btn-xs rounded-r-md"
      @focus="opened = true"
      @blur="opened = false"
      @keydown.escape.prevent="opened = false"
      :aria-expanded="opened ? 'true' : 'false'"
    >
      Open navigation
    </button>

    <!-- Thin rail/handle always visible -->
    <div
      class="absolute inset-y-0 left-0 w-3 rounded-r-full bg-primary/70 opacity-80 pointer-events-auto cursor-pointer"
      :class="{ 'animate-peek': showHint }"
      @mouseenter="opened = true"
      aria-hidden="true"
    />

    <!-- Drawer panel (overlays content) -->
    <div
      class="absolute inset-y-0 left-0 w-64 border-r border-base-300 bg-base-100 shadow-xl transform transition-transform duration-300 ease-out pointer-events-auto"
      :class="[
        opened ? 'translate-x-0' : '-translate-x-full',
        showHint && !opened ? 'hint-peek' : '',
      ]"
      @mouseenter="opened = true"
      @mouseleave="opened = false"
      @focusin="opened = true"
      @focusout="onFocusOut"
      role="navigation"
    >
      <!-- App brand (compact) -->
      <div class="px-4 py-3 border-b border-base-300 flex items-center gap-2">
        <BostelloLogo size="sm" class-name="h-6 w-auto" />
        <span class="font-semibold">Navigation</span>
      </div>

      <!-- Nav list -->
      <nav class="h-[calc(100%-48px)] overflow-y-auto p-2">
        <ul class="menu menu-lg w-full">
          <li>
            <router-link :to="'/'" :class="linkClass('/')">
              <HomeIcon class="w-5 h-5" />
              <span>Dashboard</span>
            </router-link>
          </li>

          <li class="menu-title mt-2">
            <span>Users</span>
          </li>
          <li>
            <details :open="isSectionOpen('/users')">
              <summary :class="summaryClass('/users')" @click.prevent="toggleSection('/users')">
                <UserIcon class="w-5 h-5" />
                <span>Users</span>
              </summary>
              <ul>
                <li>
                  <router-link :to="'/users'" :class="linkClass('/users', true)">
                    Hub
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/users/list'" :class="linkClass('/users/list', true)">
                    Users
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/roles'" :class="linkClass('/roles', true)">
                    Roles
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/permissions'" :class="linkClass('/permissions', true)">
                    Permissions
                  </router-link>
                </li>
              </ul>
            </details>
          </li>

          <li class="menu-title mt-2">
            <span>Agencies</span>
          </li>
          <li>
            <details :open="isSectionOpen('/agencies')">
              <summary :class="summaryClass('/agencies')" @click.prevent="toggleSection('/agencies')">
                <BuildingOffice2Icon class="w-5 h-5" />
                <span>Agencies</span>
              </summary>
              <ul>
                <li>
                  <router-link :to="'/agencies'" :class="linkClass('/agencies', true)">
                    Hub
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/agencies/list'" :class="linkClass('/agencies/list', true)">
                    Agencies
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/agencies/subscriptions'" :class="linkClass('/agencies/subscriptions', true)">
                    Subscriptions
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/agencies/onboard'" :class="linkClass('/agencies/onboard', true)">
                    Onboard broker
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/agencies/bostello-sites'" :class="linkClass('/agencies/bostello-sites', true)">
                    Bostello agency sites
                  </router-link>
                </li>
              </ul>
            </details>
          </li>

          <li class="menu-title mt-2">
            <span>Operations</span>
          </li>
          <li>
            <details :open="isSectionOpen('/properties')">
              <summary :class="summaryClass('/properties')" @click.prevent="toggleSection('/properties')">
                <HomeModernIcon class="w-5 h-5" />
                <span>Properties</span>
              </summary>
              <ul>
                <li>
                  <router-link :to="'/properties'" :class="linkClass('/properties', true)">
                    Hub
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/properties/list'" :class="linkClass('/properties/list', true)">
                    List
                  </router-link>
                </li>
                <li>
                  <router-link :to="{ path: '/properties/list', query: { tab: 'locations' } }" :class="linkClass('/properties/list', true)">
                    Locations
                  </router-link>
                </li>
                <li>
                  <router-link :to="{ path: '/properties/list', query: { tab: 'reports' } }" :class="linkClass('/properties/list', true)">
                    Reports
                  </router-link>
                </li>
                <li>
                  <router-link :to="{ path: '/properties/list', query: { tab: 'issues' } }" :class="linkClass('/properties/list', true)">
                    Issues
                  </router-link>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <details :open="isSectionOpen('/leads')">
              <summary :class="summaryClass('/leads')" @click.prevent="toggleSection('/leads')">
                <EnvelopeOpenIcon class="w-5 h-5" />
                <span>Leads</span>
              </summary>
              <ul>
                <li>
                  <router-link to="/leads" :class="linkClassLeadsList()">
                    List
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/leads/broker-contact"
                    :class="linkClassLeadsBrokerContact()"
                  >
                    Broker lead contact (Mautic)
                  </router-link>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="'/property-card-ads'" :class="linkClass('/property-card-ads')">
              <MegaphoneIcon class="w-5 h-5" />
              <span>Ads</span>
            </router-link>
          </li>
          <li>
            <router-link :to="'/search-order-config'" :class="linkClass('/search-order-config')">
              <FunnelIcon class="w-5 h-5" />
              <span>Search Config</span>
            </router-link>
          </li>
          <li>
            <details :open="isSectionOpen('/paid-extras')">
              <summary :class="summaryClass('/paid-extras')" @click.prevent="toggleSection('/paid-extras')">
                <BanknotesIcon class="w-5 h-5" />
                <span>Paid Extras</span>
              </summary>
              <ul>
                <li>
                  <router-link :to="'/paid-extras'" :class="linkClass('/paid-extras', true)">
                    Hub
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/paid-extras/agency'" :class="linkClass('/paid-extras/agency', true)">
                    Agency Extras
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/paid-extras/manage-types'" :class="linkClass('/paid-extras/manage-types', true)">
                    Manage Types
                  </router-link>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <router-link :to="'/assets'" :class="linkClass('/assets')">
              <PhotoIcon class="w-5 h-5" />
              <span>Assets</span>
            </router-link>
          </li>
          <li>
            <details :open="isSectionOpen('/integrations')">
              <summary :class="summaryClass('/integrations')" @click.prevent="toggleSection('/integrations')">
                <Cog6ToothIcon class="w-5 h-5" />
                <span>Integrations</span>
              </summary>
              <ul>
                <li>
                  <router-link :to="'/integrations'" :class="linkClassExact('/integrations')">
                    Hub
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/integrations/overview'" :class="linkClass('/integrations/overview')">
                    Overview
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/integrations/kyero'" :class="linkClass('/integrations/kyero')">
                    Kyero Jobs
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/integrations/scrapers'" :class="linkClass('/integrations/scrapers')">
                    Scrapers
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/integrations/mapbox-cron'" :class="linkClass('/integrations/mapbox-cron')">
                    Mapbox Cron
                  </router-link>
                </li>
                <li>
                  <router-link :to="'/integrations/openai-cron'" :class="linkClass('/integrations/openai-cron')">
                    OpenAI Cron
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="'/integrations/member-outbound'"
                    :class="linkClass('/integrations/member-outbound')"
                  >
                    Member outbound
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="'/integrations/meta-insights'"
                    :class="linkClass('/integrations/meta-insights')"
                  >
                    Meta insights
                  </router-link>
                </li>
              </ul>
            </details>
          </li>

          <li class="menu-title mt-2">
            <span>Account</span>
          </li>
          <li>
            <router-link :to="'/settings'" :class="linkClass('/settings')">
              <Cog6ToothIcon class="w-5 h-5" />
              <span>Settings</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>
  </aside>
</template>

<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    HomeIcon,
    UserIcon,
    KeyIcon,
    ShieldCheckIcon,
    BuildingOffice2Icon,
    CreditCardIcon,
    HomeModernIcon,
    EnvelopeOpenIcon,
    MegaphoneIcon,
    FunnelIcon,
    BanknotesIcon,
    PhotoIcon,
    Cog6ToothIcon,
  } from '@heroicons/vue/24/outline'
  import BostelloLogo from '@/components/BostelloLogo.vue'

  const route = useRoute()

  // Persist which sections are expanded across route changes so the menu doesn't
  // collapse when navigating via top nav (e.g. to /leads) and "items disappear"
  const openSections = ref<Record<string, boolean>>({})
  const SECTION_BASE_PATHS = ['/users', '/agencies', '/properties', '/leads', '/paid-extras', '/integrations'] as const

  const routeMatchesSection = (basePath: string) => {
    if (basePath === '/users') {
      return route.path.startsWith('/users') || route.path === '/roles' || route.path === '/permissions'
    }
    if (basePath === '/leads') {
      return route.path === '/leads' || route.path.startsWith('/leads/')
    }
    return route.path.startsWith(basePath)
  }

  const isSectionOpen = (basePath: string) => {
    if (basePath in openSections.value) {
      return openSections.value[basePath]
    }
    return routeMatchesSection(basePath)
  }

  const toggleSection = (basePath: string) => {
    openSections.value[basePath] = !isSectionOpen(basePath)
  }

  // When route changes, open the section that contains the current path (never close others)
  watch(
    () => route.path,
    (path) => {
      for (const basePath of SECTION_BASE_PATHS) {
        const matches =
          basePath === '/users'
            ? path.startsWith('/users') || path === '/roles' || path === '/permissions'
            : basePath === '/leads'
              ? path === '/leads' || path.startsWith('/leads/')
              : path.startsWith(basePath)
        if (matches) {
          openSections.value[basePath] = true
          break
        }
      }
    },
    { immediate: true }
  )

  const summaryClass = (basePath: string) => {
    const active = isSectionOpen(basePath)
    return ['justify-start gap-3', active ? 'active font-medium' : ''].join(' ')
  }

  const linkClass = (pathStartsWith: string, _isSub = false) => {
    const active = route.path === pathStartsWith || route.path.startsWith(pathStartsWith + '/')
    return ['justify-start gap-3', active ? 'active font-medium' : ''].join(' ')
  }

  const linkClassExact = (path: string) => {
    return ['justify-start gap-3', route.path === path ? 'active font-medium' : ''].join(' ')
  }

  /** Leads list + lead detail — not active on broker-contact subpage */
  const linkClassLeadsList = () => {
    const p = route.path
    const active =
      p === '/leads' ||
      (p.startsWith('/leads/') && p !== '/leads/broker-contact')
    return ['justify-start gap-3', active ? 'active font-medium' : ''].join(' ')
  }

  const linkClassLeadsBrokerContact = () => {
    const active = route.path === '/leads/broker-contact'
    return ['justify-start gap-3', active ? 'active font-medium' : ''].join(' ')
  }

  const opened = ref(false)
  const showHint = ref(false)

  const onFocusOut = (e: FocusEvent) => {
    const current = e.currentTarget as HTMLElement
    const next = e.relatedTarget as Node | null
    if (!next || !current.contains(next)) {
      opened.value = false
    }
  }

  onMounted(() => {
    try {
      const reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const shown = localStorage.getItem('leftDrawerHintShownV2') === 'true'
      if (!reduceMotion && !shown) {
        showHint.value = true
        setTimeout(() => {
          showHint.value = false
          localStorage.setItem('leftDrawerHintShownV2', 'true')
        }, 1800)
      }
    } catch {
      // ignore
    }
  })
</script>

<style scoped>
  @keyframes peek {
    0% {
      transform: translateX(0);
      opacity: 0;
    }
    20% {
      transform: translateX(30px);
      opacity: 1;
    }
    60% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform: translateX(0);
      opacity: 0;
    }
  }
  .animate-peek {
    animation: peek 1.4s ease-out 0.1s 1;
  }
  /* Stronger peek of the panel itself (~30px) */
  .hint-peek {
    transform: translateX(calc(-100% + 30px)) !important;
  }
</style>
